import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import FONTS from "../../../constants/fonts";
import COLORS from "../../../constants/colors";
import SIZES from "../../../constants/sizes";

const heightMap = {
  [SIZES.MEDIUM]: 38,
  [SIZES.LARGE]: 48,
  [SIZES.BIG]: 48
};

export const getHeight = size => heightMap[size];

const getSizingDefinitions = size => {
  if (size === SIZES.BIG) {
    return css`
      font-size: 18px;
      height: ${getHeight(size)}px;
    `;
  } else if (size === SIZES.LARGE) {
    return css`
      font-size: 16px;
      height: ${getHeight(size)}px;
    `;
  } else {
    // medium (default)
    return css`
      font-size: 14px;
      height: ${getHeight(size)}px;
    `;
  }
};

export const getBorderColorDefinition = ({
  focused,
  disabled,
  invalid,
  hovered
}) => {
  if (invalid) {
    return css`
      border-color: ${COLORS.STATUS_COLORS.ERROR};
    `;
  }
  if (focused) {
    return css`
      border-color: ${COLORS.ACTION_COLORS.ONELOGIN_BLUE};
    `;
  }
  if (disabled) {
    return css`
      border-color: ${COLORS.GRAY_COLORS.GRAY_94};
    `;
  }

  if (hovered) {
    return css`
      border-color: ${COLORS.GRAY_COLORS.GRAY_68};
    `;
  }

  return css`
    &:hover {
      border-color: ${COLORS.GRAY_COLORS.GRAY_68};
    }

    &:focus {
      border-color: ${COLORS.ACTION_COLORS.ONELOGIN_BLUE};
    }
  `;
};

const getBackgroundColor = ({ locked, disabled }) => {
  if (locked) {
    return "#edfaff";
  }
  if (disabled) {
    return "#f2f4f5";
  }

  return COLORS.STATUS_COLORS.WHITE;
};

const Input = styled.input`
  ${props => getSizingDefinitions(props.size)};
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${COLORS.GRAY_COLORS.GRAY_88};
  transition: border-color 0.2s;
  border-radius: 2px;
  font-family: ${FONTS.FONT_FAMILY.DEFAULT};
  color: ${COLORS.TEXT_COLORS.EMPHASIZED};
  letter-spacing: 0.2px;
  padding: 0 12px;
  line-height: normal;
  background-color: ${props => getBackgroundColor(props)};

  ${props => getBorderColorDefinition(props)};

  &::-ms-clear {
    display: none;
  }

  &::-webkit-credentials-auto-fill-button {
    visibility: hidden;
  }

  &::placeholder {
    color: #acadad;
  }
`;

Input.defaultProps = {
  type: "text",
  autoFocus: false,
  disabled: false,
  invalid: false,
  autoComplete: "off",
  autoCorrect: "off",
  spellCheck: "false",
  large: false,
  maxLength: 255,
  focused: false,
  locked: false,
  size: SIZES.MEDIUM
};

Input.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  type: PropTypes.string,
  onBlur: PropTypes.func,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  autoComplete: PropTypes.string,
  autoCorrect: PropTypes.string,
  spellCheck: PropTypes.string,
  maxLength: PropTypes.number,
  name: PropTypes.string,
  autoCapitalize: PropTypes.string,
  size: PropTypes.oneOf([SIZES.MEDIUM, SIZES.LARGE, SIZES.BIG]),
  focused: PropTypes.bool,
  locked: PropTypes.bool
};

export default Input;
